import { assign, setup } from "xstate";

import { PositiveInt } from "@src/shared/utils/types";

export type EncounterStatus =
  | "idle"
  | "connected"
  | "in_progress"
  | "finish_requested"
  | "finalizing"
  | "completed_awaiting_note"
  | "completed";

export type EncounterContext = {
  encounterId: PositiveInt | null;
  startedAtMs: number | null;
  /**
   * `completedAt` on the active encounter when finish was requested. Used to
   * ignore a reopened encounter's preserved historical note/completion until a
   * newer completion version arrives with the generated note.
   */
  completedAtBaseline: string | null;
  /**
   * Set when patient chrome navigates to the visit route for note output.
   * Prevents remounting patient detail (e.g. Visit Back → Past Visits) from
   * re-showing the generating surface and auto-navigating again.
   */
  finishHandedOff: boolean;
};

export type EncounterEvent =
  | { type: "ENCOUNTER_ID_CHANGED"; encounterId: PositiveInt | null }
  | { type: "TRANSCRIPTION_CREATED"; encounterId: PositiveInt }
  | { type: "SNAPSHOT_RECEIVED" }
  | { type: "TRANSCRIPT_SNAPSHOT_RECEIVED" }
  | { type: "TRANSCRIPT_ITEM_CREATED" }
  | { type: "FINISH_REQUESTED"; completedAtBaseline: string | null }
  | { type: "CLEAR_FINISH_REQUESTED"; encounterId: PositiveInt }
  | { type: "FINISH_HANDED_OFF"; encounterId: PositiveInt }
  | { type: "FINALIZING" }
  | { type: "COMPLETED" }
  | { type: "NOTE_READY"; encounterId: PositiveInt }
  | { type: "REOPENED" }
  | { type: "SUPERSEDED"; newEncounterId: PositiveInt }
  | { type: "PATIENT_CLEARED" };

export const encounterMachine = setup({
  types: {
    context: {} as EncounterContext,
    events: {} as EncounterEvent,
  },
  guards: {
    matchesEventEncounterId: ({ context, event }) => {
      if (
        event.type !== "CLEAR_FINISH_REQUESTED" &&
        event.type !== "NOTE_READY" &&
        event.type !== "FINISH_HANDED_OFF"
      ) {
        return false;
      }
      return (
        context.encounterId != null && context.encounterId === event.encounterId
      );
    },
    encounterIdActuallyChanged: ({ context, event }) => {
      if (event.type !== "ENCOUNTER_ID_CHANGED") return false;
      return context.encounterId !== event.encounterId;
    },
  },
  actions: {
    assignIdFromEvent: assign({
      encounterId: ({ event }) => {
        if (
          event.type === "ENCOUNTER_ID_CHANGED" ||
          event.type === "TRANSCRIPTION_CREATED"
        ) {
          return event.encounterId;
        }
        return null;
      },
    }),
    assignNewIdFromSupersededEvent: assign({
      encounterId: ({ event }) => {
        if (event.type === "SUPERSEDED") return event.newEncounterId;
        return null;
      },
    }),
    clearEncounterId: assign({ encounterId: () => null }),
    recordStartedAt: assign({
      startedAtMs: () => Date.now(),
    }),
    clearStartedAt: assign({
      startedAtMs: () => null,
    }),
    assignCompletedAtBaselineFromEvent: assign({
      completedAtBaseline: ({ event }) => {
        if (event.type === "FINISH_REQUESTED") {
          return event.completedAtBaseline;
        }
        return null;
      },
    }),
    clearCompletedAtBaseline: assign({
      completedAtBaseline: () => null,
    }),
    assignFinishHandedOff: assign({
      finishHandedOff: () => true,
    }),
    clearFinishHandedOff: assign({
      finishHandedOff: () => false,
    }),
  },
}).createMachine({
  id: "encounter",
  initial: "idle",
  context: {
    encounterId: null,
    startedAtMs: null,
    completedAtBaseline: null,
    finishHandedOff: false,
  },
  on: {
    ENCOUNTER_ID_CHANGED: {
      guard: "encounterIdActuallyChanged",
      target: ".idle",
      actions: [
        "assignIdFromEvent",
        "clearStartedAt",
        "clearCompletedAtBaseline",
        "clearFinishHandedOff",
      ],
    },
    TRANSCRIPTION_CREATED: {
      target: ".idle",
      actions: [
        "assignIdFromEvent",
        "clearStartedAt",
        "clearCompletedAtBaseline",
        "clearFinishHandedOff",
      ],
    },
    PATIENT_CLEARED: {
      target: ".idle",
      actions: [
        "clearEncounterId",
        "clearStartedAt",
        "clearCompletedAtBaseline",
        "clearFinishHandedOff",
      ],
    },
    FINISH_HANDED_OFF: {
      guard: "matchesEventEncounterId",
      actions: ["assignFinishHandedOff"],
    },
  },
  states: {
    idle: {
      on: {
        SNAPSHOT_RECEIVED: { target: "connected" },
        TRANSCRIPT_SNAPSHOT_RECEIVED: { target: "in_progress" },
        TRANSCRIPT_ITEM_CREATED: { target: "in_progress" },
        FINISH_REQUESTED: {
          target: "finish_requested",
          actions: [
            "assignCompletedAtBaselineFromEvent",
            "clearFinishHandedOff",
          ],
        },
        FINALIZING: { target: "finalizing" },
        COMPLETED: { target: "completed_awaiting_note" },
        REOPENED: {
          target: "in_progress",
          actions: ["clearCompletedAtBaseline", "clearFinishHandedOff"],
        },
      },
    },
    connected: {
      entry: ["recordStartedAt"],
      on: {
        SNAPSHOT_RECEIVED: { target: "connected", reenter: false },
        TRANSCRIPT_SNAPSHOT_RECEIVED: { target: "in_progress" },
        TRANSCRIPT_ITEM_CREATED: { target: "in_progress" },
        FINISH_REQUESTED: {
          target: "finish_requested",
          actions: [
            "assignCompletedAtBaselineFromEvent",
            "clearFinishHandedOff",
          ],
        },
        FINALIZING: { target: "finalizing" },
        COMPLETED: { target: "completed_awaiting_note" },
        REOPENED: {
          target: "in_progress",
          actions: ["clearCompletedAtBaseline", "clearFinishHandedOff"],
        },
      },
    },
    in_progress: {
      on: {
        FINISH_REQUESTED: {
          target: "finish_requested",
          actions: [
            "assignCompletedAtBaselineFromEvent",
            "clearFinishHandedOff",
          ],
        },
        FINALIZING: { target: "finalizing" },
        COMPLETED: { target: "completed_awaiting_note" },
        SUPERSEDED: {
          target: "connected",
          actions: [
            "assignNewIdFromSupersededEvent",
            "clearCompletedAtBaseline",
            "clearFinishHandedOff",
          ],
        },
      },
    },
    finish_requested: {
      on: {
        FINALIZING: { target: "finalizing" },
        COMPLETED: { target: "completed_awaiting_note" },
        NOTE_READY: {
          target: "completed",
          guard: "matchesEventEncounterId",
          actions: ["clearCompletedAtBaseline"],
        },
        CLEAR_FINISH_REQUESTED: {
          target: "in_progress",
          guard: "matchesEventEncounterId",
          actions: ["clearCompletedAtBaseline", "clearFinishHandedOff"],
        },
        REOPENED: {
          target: "in_progress",
          actions: ["clearCompletedAtBaseline", "clearFinishHandedOff"],
        },
      },
    },
    finalizing: {
      on: {
        COMPLETED: { target: "completed_awaiting_note" },
        NOTE_READY: {
          target: "completed",
          guard: "matchesEventEncounterId",
          actions: ["clearCompletedAtBaseline"],
        },
        REOPENED: {
          target: "in_progress",
          actions: ["clearCompletedAtBaseline", "clearFinishHandedOff"],
        },
      },
    },
    completed_awaiting_note: {
      entry: ["clearStartedAt"],
      on: {
        NOTE_READY: {
          target: "completed",
          guard: "matchesEventEncounterId",
          actions: ["clearCompletedAtBaseline"],
        },
        REOPENED: {
          target: "in_progress",
          actions: ["clearCompletedAtBaseline", "clearFinishHandedOff"],
        },
        SUPERSEDED: {
          target: "connected",
          actions: [
            "assignNewIdFromSupersededEvent",
            "clearCompletedAtBaseline",
            "clearFinishHandedOff",
          ],
        },
      },
    },
    completed: {
      entry: [
        "clearStartedAt",
        "clearCompletedAtBaseline",
        "clearFinishHandedOff",
      ],
      on: {
        REOPENED: {
          target: "in_progress",
          actions: ["clearCompletedAtBaseline", "clearFinishHandedOff"],
        },
        SUPERSEDED: {
          target: "connected",
          actions: [
            "assignNewIdFromSupersededEvent",
            "clearCompletedAtBaseline",
            "clearFinishHandedOff",
          ],
        },
      },
    },
  },
});
